import { useEffect, useRef, useState } from "react";
import { ADS_CONFIG, currentAdConsent, loadAdsSDK, localPublicURL, openAdPreferences, useAdSidebarEnabled, type AdController } from "../lib/ads";

/** The SDK owns a shadow root only; it never receives catalog, selection or file state. */
export function AdRail({ onCollapsed }: { onCollapsed: (collapsed: boolean) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const enabled = useAdSidebarEnabled();
  const [localFailure, setLocalFailure] = useState(false);
  useEffect(() => {
    onCollapsed(!enabled);
    if (!enabled || !host.current) return;
    let alive = true;
    let controller: AdController | undefined;
    const element = host.current;
    element.hidden = false;
    const status = (event: Event) => {
      const state = (event as CustomEvent<{ state: string }>).detail.state;
      element.dataset.adState = state;
      onCollapsed(state === "paused");
    };
    const preferences = (event: Event) => { event.preventDefault(); openAdPreferences(); };
    element.addEventListener("picplayer:ads-status", status);
    element.addEventListener("picplayer:ad-preferences", preferences);
    // Do not load scripts or make ad requests while the rail is below its breakpoint.
    const media = window.matchMedia(`(min-width: ${ADS_CONFIG.minViewportWidth}px)`);
    let loading = false;
    const mount = async () => {
      if (!media.matches || controller || loading || !alive) return;
      loading = true;
      try {
        const sdk = await loadAdsSDK();
        if (!alive) return;
        setLocalFailure(false);
        controller = sdk.mount({ element, appId: ADS_CONFIG.appId, managerUrl: ADS_CONFIG.managerUrl, minViewportWidth: ADS_CONFIG.minViewportWidth, cssUrl: localPublicURL("ads/rail.css"), privacyUrl: localPublicURL("privacy.html"), consent: currentAdConsent() });
      } catch { if (alive) setLocalFailure(true); }
      finally { loading = false; }
    };
    const resize = () => { void mount(); };
    media.addEventListener("change", resize);
    void mount();
    return () => {
      alive = false;
      media.removeEventListener("change", resize);
      element.removeEventListener("picplayer:ads-status", status);
      element.removeEventListener("picplayer:ad-preferences", preferences);
      controller?.destroy();
    };
  }, [enabled, onCollapsed]);
  if (!enabled) return null;
  return <aside className="shared-ad-rail" aria-label="Sponsored advertisements" data-ad-client="focalfolio">
    <div ref={host} className="ad-sdk-host" />
    {localFailure ? <div className="ad-local-fallback"><header>SPONSORED<button type="button" onClick={openAdPreferences}>Ad choices</button></header><div><span>✦</span><h3>A little space.<br />More possibility.</h3><p>Advertising helps support our creative tools. Your files stay on your computer.</p></div></div> : null}
  </aside>;
}
